import React, { useState, useEffect, useRef, useMemo } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, Platform, ActivityIndicator, Image } from 'react-native';
import { useParams, useNavigate } from 'react-router-dom';
import WebScrollView from '../components/WebScrollView';
import { Bot, Send, ArrowLeft, User, Paperclip, X } from '../lib/icons';
import { showAlert } from '../lib/platform';
import { supabase } from '../lib/supabase';
import { getAccessibleHomes } from '../lib/homeMembership';
import ChatMessage from '../components/chat/ChatMessage';
import { useConversationMessages } from '../hooks/useConversationMessages';
import { useMarkMessagesRead } from '../hooks/useMarkMessagesRead';

const HONEYBOT_AVATAR_URL = "https://storage.googleapis.com/hdwebsite/images/HoneyBot%20Avatar.png";

interface Conversation {
  id: string;
  homeowner_id: string;
  type: 'honeybot' | 'home_manager';
  home_manager_id: string | null;
  thread_id: string | null;
  home_manager?: {
    id: string;
    full_name: string;
    profile_photo_url: string | null;
  };
}

interface Profile {
  full_name: string;
  phone_number: string;
  created_at: string;
}

interface Home {
  id: string;
  address: string;
  city: string;
  state: string;
}

interface InventoryItem {
  id: string;
  name: string;
  category: string;
  model_number: string;
  purchase_date: string;
  purchase_price: number;
}

const ConversationDetail = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const scrollViewRef = useRef<any>(null);

  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [userProfile, setUserProfile] = useState<Profile | null>(null);
  const [userHomes, setUserHomes] = useState<Home[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [avatarError, setAvatarError] = useState(false);
  const [managerAvatarError, setManagerAvatarError] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const lastMessageIdRef = useRef<string | null>(null);

  const { messages: dbMessages, loading: messagesLoading, refetch } = useConversationMessages(conversationId || null);
  const [optimisticMessages, setOptimisticMessages] = useState<Array<{
    id: string;
    conversation_id: string;
    sender_type: 'homeowner' | 'manager' | 'ai';
    sender_id: string | null;
    content: string;
    attachment_url: string | null;
    attachment_name: string | null;
    attachment_size: number | null;
    attachment_type: string | null;
    created_at: string;
  }>>([]);
  
  // Combine database messages with optimistic updates, avoiding duplicates
  const messages = useMemo(() => {
    const messageMap = new Map<string, typeof dbMessages[0]>();
    
    // Add database messages first
    dbMessages.forEach(msg => {
      messageMap.set(msg.id, msg);
    });
    
    // Add optimistic messages only if they don't exist in database
    optimisticMessages.forEach(optMsg => {
      const exists = Array.from(messageMap.values()).some(dbMsg => {
        const contentMatch = dbMsg.content === optMsg.content;
        const attachmentMatch = dbMsg.attachment_url === optMsg.attachment_url && 
                                optMsg.attachment_url !== null;
        const timeMatch = Math.abs(new Date(dbMsg.created_at).getTime() - new Date(optMsg.created_at).getTime()) < 5000;
        
        // Match if content matches OR attachment matches, and time is close
        return (contentMatch || attachmentMatch) && timeMatch;
      });
      if (!exists) {
        messageMap.set(optMsg.id, optMsg as typeof dbMessages[0]);
      }
    });
    
    return Array.from(messageMap.values()).sort((a, b) => 
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  }, [dbMessages, optimisticMessages]);
  
  // Mark messages as read when conversation is viewed
  useMarkMessagesRead(conversationId || null);

  useEffect(() => {
    if (conversationId) {
      setHasScrolledToBottom(false); // Reset scroll state when conversation changes
      lastMessageIdRef.current = null; // Reset last message ID
      Promise.all([
        fetchConversation(),
        fetchUserData()
      ]).finally(() => {
        setInitialLoading(false);
      });
    }
  }, [conversationId]);

  // Scroll to bottom when initial loading completes and messages are available
  useEffect(() => {
    if (!initialLoading && !messagesLoading && messages.length > 0 && !hasScrolledToBottom) {
      // Use multiple attempts with increasing delays to ensure content is rendered
      const scrollToBottom = () => {
        if (scrollViewRef.current) {
          scrollViewRef.current.scrollToEnd?.({ animated: false });
        }
      };

      // Try immediately, then again after layout completes
      requestAnimationFrame(() => {
        scrollToBottom();
        // Try again after delays to account for layout and rendering
        setTimeout(() => {
          scrollToBottom();
          setTimeout(() => {
            scrollToBottom();
            setHasScrolledToBottom(true);
          }, 200);
        }, 200);
      });
    }
  }, [initialLoading, messagesLoading, messages.length, hasScrolledToBottom]);

  // Scroll to bottom when new messages arrive (after initial scroll)
  useEffect(() => {
    if (hasScrolledToBottom && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      const currentLastMessageId = lastMessage?.id;

      // Check if a new message was added
      if (currentLastMessageId && currentLastMessageId !== lastMessageIdRef.current) {
        lastMessageIdRef.current = currentLastMessageId;
        
        // Small delay to ensure new message is rendered
        const timeoutId = setTimeout(() => {
          scrollViewRef.current?.scrollToEnd?.({ animated: true });
        }, 150);

        return () => clearTimeout(timeoutId);
      }
    } else if (messages.length > 0 && !hasScrolledToBottom) {
      // Update ref even if we haven't scrolled yet
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.id) {
        lastMessageIdRef.current = lastMessage.id;
      }
    }
  }, [messages, hasScrolledToBottom]);

  // Clear optimistic messages when real messages arrive (to avoid duplicates)
  useEffect(() => {
    if (dbMessages.length > 0 && optimisticMessages.length > 0) {
      // Match optimistic messages with real ones by content and approximate time
      const matchedIds = new Set<string>();
      
      optimisticMessages.forEach(optMsg => {
        const matchingRealMsg = dbMessages.find(dbMsg => {
          const contentMatch = dbMsg.content === optMsg.content;
          const attachmentMatch = dbMsg.attachment_url === optMsg.attachment_url && 
                                  optMsg.attachment_url !== null;
          const timeMatch = Math.abs(new Date(dbMsg.created_at).getTime() - new Date(optMsg.created_at).getTime()) < 10000; // 10 second window
          
          // Match if content matches OR attachment matches, and time is close
          return (contentMatch || attachmentMatch) && timeMatch;
        });
        
        if (matchingRealMsg) {
          matchedIds.add(optMsg.id);
        }
      });
      
      // Only remove matched optimistic messages (keep ones that haven't arrived yet)
      if (matchedIds.size > 0) {
        setOptimisticMessages(prev => prev.filter(m => !matchedIds.has(m.id)));
      }
    }
  }, [dbMessages, optimisticMessages]);

  const fetchConversation = async () => {
    try {
      const { data, error } = await supabase
        .from('conversations')
        .select(`
          *,
          home_manager:profiles!conversations_home_manager_id_fkey (
            id,
            full_name,
            profile_photo_url
          )
        `)
        .eq('id', conversationId)
        .single();

      if (error) throw error;
      setConversation(data);
    } catch (error) {
      console.error('Error fetching conversation:', error);
    }
  };

  const fetchUserData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, phone_number, created_at')
        .eq('id', user.id)
        .single();

      setUserProfile(profile);

      // Fetch homes
      const homes = await getAccessibleHomes(user.id);

      setUserHomes(homes || []);

      // Fetch inventory for HoneyBot context
      if (homes && homes.length > 0) {
        const { data: inventory } = await supabase
          .from('inventory_items')
          .select('id, name, category, model_number, purchase_date, purchase_price')
          .in('home_id', homes.map(h => h.id));

        setInventoryItems(inventory || []);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const handleFileSelect = () => {
    if (Platform.OS === 'web' && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        showAlert('File size must be less than 50MB');
        return;
      }
      setSelectedFile(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadFile = async (file: File): Promise<string> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const filePath = `message-attachments/${user.id}/${timestamp}_${sanitizedFileName}`;

    const { error: uploadError } = await supabase.storage
      .from('message-attachments')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('message-attachments')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const handleSubmit = async () => {
    if ((!input.trim() && !selectedFile) || loading || !conversation) return;

    const userMessage = input.trim();
    setInput('');
    setLoading(true);
    setUploadingFile(selectedFile !== null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Handle file upload first if present (for home manager conversations)
      let attachmentUrl: string | null = null;
      let attachmentName: string | null = null;
      let attachmentSize: number | null = null;
      let attachmentType: string | null = null;

      if (selectedFile && conversation.type !== 'honeybot') {
        try {
          attachmentUrl = await uploadFile(selectedFile);
          attachmentName = selectedFile.name;
          attachmentSize = selectedFile.size;
          attachmentType = selectedFile.type || 'application/octet-stream';
          // Clear file selection after successful upload
          setSelectedFile(null);
          setUploadingFile(false);
        } catch (error) {
          console.error('Error uploading file:', error);
          showAlert('Failed to upload file. Please try again.');
          setLoading(false);
          setUploadingFile(false);
          return;
        }
      }

      // Optimistically add user message immediately (with attachment if uploaded)
      const tempUserMessageId = `temp-${Date.now()}-user`;
      const tempUserMessage = {
        id: tempUserMessageId,
        conversation_id: conversation.id,
        sender_type: 'homeowner' as const,
        sender_id: user.id,
        content: userMessage,
        attachment_url: attachmentUrl,
        attachment_name: attachmentName,
        attachment_size: attachmentSize,
        attachment_type: attachmentType,
        created_at: new Date().toISOString()
      };
      setOptimisticMessages(prev => [...prev, tempUserMessage]);

      if (conversation.type === 'honeybot') {
        // For HoneyBot, call the AI chat function
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('No authenticated session');

        const response = await fetch('https://wqznjpzkejoxiokudivc.supabase.co/functions/v1/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            message: userMessage,
            userProfile,
            userHomes,
            inventoryItems,
            threadId: conversation.thread_id,
            conversationId: conversation.id
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Chat API error:', errorText);
          throw new Error('Failed to get AI response');
        }

        const responseData = await response.json();
        console.log('Chat API response:', responseData);
        
        // Optimistically add assistant response immediately
        const tempAssistantMessageId = `temp-${Date.now()}-assistant`;
        const tempAssistantMessage = {
          id: tempAssistantMessageId,
          conversation_id: conversation.id,
          sender_type: 'ai' as const,
          sender_id: null,
          content: responseData.message || 'I apologize, but I encountered an error.',
          created_at: new Date().toISOString()
        };
        setOptimisticMessages(prev => [...prev, tempAssistantMessage]);
        
        // Update conversation with thread_id if we got one back and didn't have it
        if (responseData.threadId && !conversation.thread_id) {
          await supabase
            .from('conversations')
            .update({ thread_id: responseData.threadId })
            .eq('id', conversation.id);
          
          // Update local state
          setConversation(prev => prev ? { ...prev, thread_id: responseData.threadId } : null);
        }

        // Messages are inserted by the chat function
        // Refetch multiple times to ensure we get the messages
        setTimeout(() => {
          console.log('Refetching messages after 1.5s...');
          refetch();
        }, 1500);
        
        setTimeout(() => {
          console.log('Refetching messages after 3s...');
          refetch();
        }, 3000);
        
        // Clear optimistic messages only after we confirm real messages arrived
        // This happens in the useEffect that watches dbMessages
      } else {
        // For home manager, file upload was already handled above before optimistic message
        // Insert the message with or without attachment
        const messageData: any = {
          conversation_id: conversation.id,
          sender_type: 'homeowner',
          sender_id: user.id,
        };

        if (userMessage) {
          messageData.content = userMessage;
        }

        if (attachmentUrl) {
          messageData.attachment_url = attachmentUrl;
          messageData.attachment_name = attachmentName;
          messageData.attachment_size = attachmentSize;
          messageData.attachment_type = attachmentType;
        }

        const { error: insertError } = await supabase
          .from('conversation_messages')
          .insert(messageData);

        if (insertError) {
          // Remove optimistic message on error
          setOptimisticMessages(prev => prev.filter(m => m.id !== tempUserMessage.id));
          throw insertError;
        }
        
        // Don't clear optimistic message immediately - let realtime subscription handle it
        // The useEffect will clear it when real message arrives
      }

    } catch (error) {
      console.error('Error sending message:', error);
      // Keep user message but add error message
      const errorMessage = {
        id: `error-${Date.now()}`,
        conversation_id: conversation.id,
        sender_type: 'ai' as const,
        sender_id: null,
        content: 'I apologize, but I encountered an error. Please try again.',
        created_at: new Date().toISOString()
      };
      setOptimisticMessages(prev => {
        // Remove user message if it's still there, but keep error
        const filtered = prev.filter(m => m.id !== tempUserMessage.id);
        return [...filtered, errorMessage];
      });
      showAlert('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getConversationTitle = () => {
    if (!conversation) return 'Loading...';
    
    if (conversation.type === 'honeybot') {
      return 'HoneyBot AI';
    } else {
      return conversation.home_manager?.full_name || 'Your Home Manager';
    }
  };

  const getConversationSubtitle = () => {
    if (!conversation) return '';
    
    if (conversation.type === 'honeybot') {
      return 'AI Assistant';
    } else {
      return 'Home Manager';
    }
  };

  if (initialLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#000000" />
      </View>
    );
  }

  if (!conversation) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Conversation not found</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Pressable
          onPress={() => navigate('/conversations')}
          style={styles.backButton}
        >
          <ArrowLeft size={24} color="#000000" />
        </Pressable>
        
        <View style={styles.headerInfo}>
          <View style={styles.headerAvatar}>
            {conversation.type === 'honeybot' ? (
              <View style={[styles.avatar, styles.avatarBot]}>
                {!avatarError ? (
                  <Image
                    source={{ uri: HONEYBOT_AVATAR_URL }}
                    style={styles.avatarImage}
                    onError={() => setAvatarError(true)}
                  />
                ) : (
                  <Bot size={20} color="#ffffff" />
                )}
              </View>
            ) : (
              <View style={[styles.avatar, styles.avatarManager]}>
                {conversation.home_manager?.profile_photo_url && !managerAvatarError ? (
                  <Image
                    source={{ uri: conversation.home_manager.profile_photo_url }}
                    style={styles.avatarImage}
                    onError={() => setManagerAvatarError(true)}
                  />
                ) : (
                  <User size={20} color="#ffffff" />
                )}
              </View>
            )}
          </View>
          <View style={styles.headerText}>
            <Text style={styles.headerTitle}>{getConversationTitle()}</Text>
            <Text style={styles.headerSubtitle}>{getConversationSubtitle()}</Text>
          </View>
        </View>
      </View>

      {/* Messages */}
      <WebScrollView ref={scrollViewRef} style={styles.messagesContainer}>
        <View style={styles.messagesList}>
          {messages.map((message, index) => {
            const isUserMessage = message.sender_type === 'homeowner';
            const isLastMessage = index === messages.length - 1;
            return (
              <View
                key={message.id}
                style={[
                  styles.messageWrapper,
                  isUserMessage ? styles.messageWrapperUser : styles.messageWrapperOther,
                  isLastMessage && styles.messageWrapperLast
                ]}
              >
                <ChatMessage
                  role={isUserMessage ? 'user' : 'assistant'}
                  content={message.content || ''}
                  attachmentUrl={message.attachment_url}
                  attachmentName={message.attachment_name}
                  attachmentSize={message.attachment_size}
                  attachmentType={message.attachment_type}
                  avatarUrl={
                    !isUserMessage && conversation.type === 'honeybot' 
                      ? HONEYBOT_AVATAR_URL 
                      : !isUserMessage && conversation.type === 'home_manager' && conversation.home_manager?.profile_photo_url
                      ? conversation.home_manager.profile_photo_url
                      : undefined
                  }
                  onAvatarError={() => {
                    if (conversation.type === 'honeybot') {
                      setAvatarError(true);
                    } else {
                      setManagerAvatarError(true);
                    }
                  }}
                />
              </View>
            );
          })}
          
          {loading && (
            <View style={[styles.messageWrapperOther, styles.messageWrapperLast]}>
              <View style={styles.typingIndicator}>
                <ActivityIndicator size="small" color="#6b7280" />
                <Text style={styles.typingText}>Typing...</Text>
              </View>
            </View>
          )}
        </View>
      </WebScrollView>

      {/* Input */}
      <View style={styles.inputWrapper}>
        {Platform.OS === 'web' && (
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            onChange={handleFileChange}
            accept="*/*"
          />
        )}
        {selectedFile && (
          <View style={styles.selectedFileContainer}>
            <Text style={styles.selectedFileName} numberOfLines={1}>
              {selectedFile.name}
            </Text>
            <Pressable
              onPress={() => setSelectedFile(null)}
              style={styles.removeFileButton}
            >
              <X size={16} color="#6b7280" />
            </Pressable>
          </View>
        )}
        <View style={styles.inputContainer}>
          {conversation.type !== 'honeybot' && (
            <Pressable
              onPress={handleFileSelect}
              style={styles.attachButton}
              disabled={loading || uploadingFile}
            >
              <Paperclip size={20} color={loading || uploadingFile ? "#9ca3af" : "#6b7280"} />
            </Pressable>
          )}
          <TextInput
            style={styles.input}
            placeholder={
              selectedFile 
                ? 'Add a message (optional)...'
                : conversation.type === 'honeybot'
                  ? 'Ask HoneyBot anything...'
                  : 'Send a message...'
            }
            value={input}
            onChangeText={setInput}
            multiline
            maxLength={2000}
            onSubmitEditing={handleSubmit}
            editable={!loading && !uploadingFile}
          />
          <Pressable
            onPress={handleSubmit}
            disabled={(!input.trim() && !selectedFile) || loading || uploadingFile}
            style={[
              styles.sendButton,
              ((!input.trim() && !selectedFile) || loading || uploadingFile) && styles.sendButtonDisabled
            ]}
          >
            {uploadingFile ? (
              <ActivityIndicator size="small" color="#ffffff" />
            ) : (
              <Send size={20} color={(!input.trim() && !selectedFile) || loading ? '#9ca3af' : '#ffffff'} />
            )}
          </Pressable>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      minHeight: 'var(--app-height)' as any,
      display: 'flex' as any,
    }),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  errorText: {
    fontSize: 16,
    color: '#ef4444',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  backButton: {
    marginRight: 12,
    padding: 4,
  },
  headerInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  headerAvatar: {
    marginRight: 12,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
  },
  avatarBot: {
    backgroundColor: '#3b82f6',
  },
  avatarManager: {
    backgroundColor: '#10b981',
  },
  headerText: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: '600',
    color: '#000000',
  },
  headerSubtitle: {
    fontSize: 13,
    color: '#6b7280',
  },
  messagesContainer: {
    flex: 1,
    backgroundColor: '#f9fafb',
    ...(Platform.OS === 'web' && {
      paddingBottom: 150, // Add padding to account for fixed input container + selected file
    }),
    ...(Platform.OS !== 'web' && {
      paddingBottom: 20,
    }),
  },
  messagesList: {
    padding: 16,
    paddingBottom: 48,
  },
  messageWrapper: {
    marginBottom: 16,
  },
  messageWrapperLast: {
    marginBottom: 32,
  },
  messageWrapperUser: {
    alignItems: 'flex-end',
  },
  messageWrapperOther: {
    alignItems: 'flex-start',
  },
  typingIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 18,
    gap: 8,
  },
  typingText: {
    fontSize: 14,
    color: '#6b7280',
  },
  inputWrapper: {
    backgroundColor: '#ffffff',
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    ...(Platform.OS === 'web' && {
      position: 'fixed' as any,
      bottom: 64, // Position above the navbar (navbar height = 64px)
      left: 0,
      right: 0,
      zIndex: 40,
      maxWidth: '100%',
    }),
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
    minHeight: 60,
    ...(Platform.OS !== 'web' && {
      paddingBottom: 12,
    }),
  },
  input: {
    flex: 1,
    backgroundColor: '#f3f4f6',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 10,
    fontSize: 16,
    maxHeight: 100,
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as any,
    }),
  },
  sendButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#000000',
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendButtonDisabled: {
    backgroundColor: '#e5e7eb',
  },
  attachButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectedFileContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    gap: 8,
  },
  selectedFileName: {
    flex: 1,
    fontSize: 13,
    color: '#374151',
  },
  removeFileButton: {
    padding: 4,
  },
});

export default ConversationDetail;
